import {
  AlertDialog,
  Badge,
  Button,
  Flex,
  Grid,
  Link,
  Separator,
  Text,
} from "@radix-ui/themes";
import { Link as InertiaLink, router } from "@inertiajs/react";
import { CalendarClock, Trash2, User } from "lucide-react";
import { ManagerBlogsIndexBlog } from "@/types/manager_blogs";
import { badgeColors } from "@/shared/constants/constants";

interface Props {
  blog: ManagerBlogsIndexBlog;
  /** Rendered inside a mobile card that already shows this record's identity, so
   *  drop whatever the card is already saying. Each panel keeps what the card can't. */
  compact?: boolean;
}

const patch = (path: string) =>
  router.patch(path, {}, { preserveState: true, preserveScroll: true });

export default function BlogDetailPanel({ blog, compact = false }: Props) {
  return (
    <Flex direction="column" gap="4">
      {!compact && (
        <>
          <Flex direction="column" gap="2">
            {blog.public_path ? (
              <Link href={blog.public_path} size="5" weight="bold">
                {blog.title}
              </Link>
            ) : (
              <Text size="5" weight="bold">
                {blog.title}
              </Text>
            )}
            <Flex align="center" gap="1">
              <User size={14} />
              <Text size="2" color="gray">
                {blog.author}
              </Text>
            </Flex>
          </Flex>
          <Separator size="4" />
        </>
      )}

      <Flex align="center" justify="between" gap="2" wrap="wrap">
        <Flex align="center" gap="1">
          <CalendarClock size={14} />
          <Text size="2" color="gray">
            {/* The date says something different in each state, so it is worded
                rather than printed bare. */}
            {blog.status === "draft"
              ? "Not scheduled"
              : blog.status === "scheduled"
                ? `Publishes ${blog.publish_at}`
                : `Published ${blog.publish_at}`}
          </Text>
        </Flex>
        <Badge color={badgeColors[blog.status]} variant="soft">
          <Text style={{ textTransform: "capitalize" }}>{blog.status}</Text>
        </Badge>
      </Flex>

      <Separator size="4" />

      {/* Three states out of two routes: `publish_path` writes the current time,
          `unpublish_path` clears it. "Cancel schedule" is the second one under a
          label that says what it does to a post nobody has seen yet. */}
      <Grid columns="2" gap="2">
        {blog.status !== "published" && (
          <Button
            variant="soft"
            color="green"
            size="2"
            onClick={() => patch(blog.publish_path)}
          >
            Publish now
          </Button>
        )}
        {blog.status === "published" && (
          <Button
            variant="soft"
            color="crimson"
            size="2"
            onClick={() => patch(blog.unpublish_path)}
          >
            Unpublish
          </Button>
        )}
        {blog.status === "scheduled" && (
          <Button
            variant="soft"
            color="gray"
            size="2"
            onClick={() => patch(blog.unpublish_path)}
          >
            Cancel schedule
          </Button>
        )}
        <Button asChild color="gray" variant="soft" size="2">
          <InertiaLink href={blog.edit_path}>Edit Post</InertiaLink>
        </Button>
        {/* A plain anchor, not an Inertia Link: the public post is a HAML page
            in the /fb embed, not an Inertia route. */}
        {blog.public_path && (
          <Button asChild variant="soft" color="gray" size="2">
            <a href={blog.public_path}>View Public Page</a>
          </Button>
        )}
      </Grid>

      {/* Kept out of the grid above and behind its own separator: the others are
          reversible, this one is not. Same shape as EventDetailPanel. */}
      <Separator size="4" />
      <AlertDialog.Root>
        <AlertDialog.Trigger>
          <Button variant="soft" color="red" size="2">
            <Trash2 size={14} />
            Delete Post
          </Button>
        </AlertDialog.Trigger>
        <AlertDialog.Content maxWidth="450px">
          <AlertDialog.Title>Delete {blog.title}?</AlertDialog.Title>
          <AlertDialog.Description size="2">
            This cannot be undone.
            {blog.status === "published" &&
              " The post is live, so its public link will start returning a 404."}
          </AlertDialog.Description>

          <Flex gap="3" mt="4" justify="end">
            <AlertDialog.Cancel>
              <Button variant="soft" color="gray">
                Keep
              </Button>
            </AlertDialog.Cancel>
            <AlertDialog.Action>
              <Button
                variant="solid"
                color="red"
                onClick={() => router.delete(blog.destroy_path)}
              >
                Yes, delete it
              </Button>
            </AlertDialog.Action>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>
    </Flex>
  );
}
